// Agent settings (the owner-agents prototype): per agent, each action on its list as Automatic, Ask
// first or Never, never above the action's maximum (money and every other customer message stay
// "ask first"); the setting is per action, so it applies to every agent that has it. Then the
// agent's monthly budget and the company's cap, the receptionist's hours, and the notes the agent
// keeps for itself. Only the owner changes these (agents.manage); the manager reads them.

import { useEffect, useState } from 'react';
import { money } from '../books/model.ts';
import { Empty, Panel, useAct, useQuery, useToast } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import {
  DAYS,
  KIND_WORDS,
  POLICIES,
  POLICY_LABEL,
  canChoose,
  hoursText,
  initials,
  limitText,
  parseBudget,
  parseCount,
  parsePercent,
  policyCaption,
  roleLabel,
  type Policy,
  type ReceptionistHours,
  type ToolLimit,
  type ToolPolicy,
} from './model.ts';
import { Lock } from '../shared/ApprovalsInbox.tsx';
import type { AgentsProps } from './props.ts';
import { Moved } from '../blueprint/Moved.tsx';
import { useDesign } from '../blueprint/design.ts';
import type { AgentPolicies, AgentsList, MemoryNote } from './types.ts';

const HOURS_KEY = 'agents.receptionist.hours';
const WARN_KEY = 'agents.budget.warnPercent';

const dollars = (cents: number | null) => (cents === null ? '' : String(cents / 100));

/**
 * An agent's limits on an action (M10, agents.limit.<action>): a ceiling where it carries an amount,
 * and how many a day. Over either, what would run on its own asks first, saying why; money asks
 * first anyway, and its card says when it is over. Blank is no limit; Default goes back to the action's.
 */
function LimitForm({
  t,
  limit,
  props,
  onSaved,
}: {
  t: ToolPolicy;
  limit: ToolLimit;
  props: AgentsProps;
  onSaved: () => Promise<void>;
}) {
  const [max, setMax] = useState(dollars(limit.maxCents));
  const [day, setDay] = useState(limit.perDay === null ? '' : String(limit.perDay));
  useEffect(() => {
    setMax(dollars(limit.maxCents));
    setDay(limit.perDay === null ? '' : String(limit.perDay));
  }, [t.action, limit.maxCents, limit.perDay]);
  const act = useAct(props.toast, onSaved);
  const cents = limit.hasAmount ? parseBudget(max) : 'none';
  // a count only where it runs on its own: asking first anyway, a count changes nothing
  const counts = t.current === 'auto';
  const count = counts ? parseCount(day) : 'none';
  const ok = cents !== null && count !== null;
  const key = `agents.limit.${t.action}`;
  const ro = !props.isOwner;
  return (
    <form
      className="pol-limit"
      aria-label={`Limits on ${t.title}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok || ro) return;
        void act.run(
          'settings.set',
          {
            key,
            value: {
              ...(limit.hasAmount ? { maxCents: cents === 'none' ? null : cents } : {}),
              ...(counts ? { perDay: count === 'none' ? null : count } : {}),
            },
          },
          `${t.title}: limits saved for every agent.`,
        );
      }}
    >
      {limit.hasAmount && (
        <label>
          <span className="flabel">Up to ($)</span>
          <input
            className="field"
            inputMode="decimal"
            value={max}
            disabled={ro}
            placeholder="No ceiling"
            aria-invalid={cents === null}
            onChange={(e) => setMax(e.target.value)}
          />
        </label>
      )}
      {counts && (
        <label>
          <span className="flabel">Times a day</span>
          <input
            className="field"
            inputMode="numeric"
            value={day}
            disabled={ro}
            placeholder="Any"
            aria-invalid={count === null}
            onChange={(e) => setDay(e.target.value)}
          />
        </label>
      )}
      {!ro && (
        <div className="row-actions">
          <button className="btn quiet" type="submit" disabled={act.busy || !ok}>
            Save limits
          </button>
          {limit.set && (
            <button
              className="btn quiet"
              type="button"
              disabled={act.busy}
              onClick={() =>
                void act.run('settings.clear', { key }, `${t.title}: back to its default limits.`)
              }
            >
              Default
            </button>
          )}
        </div>
      )}
      <p className="cap small">
        {limitText(limit, money)}.{' '}
        {t.current === 'auto'
          ? 'Over a limit, it asks first and says why.'
          : 'It asks first anyway; over a limit, its request says so.'}
        {limit.set &&
          ` The default is ${limitText(
            { ...limit, maxCents: limit.defaultMaxCents, perDay: limit.defaultPerDay },
            money,
          ).toLowerCase()}.`}
      </p>
    </form>
  );
}

function PolicyRow({
  t,
  props,
  onSet,
  onSaved,
}: {
  t: ToolPolicy;
  props: AgentsProps;
  onSet: (t: ToolPolicy, p: Policy | null) => void;
  onSaved: () => Promise<void>;
}) {
  return (
    <li className="pol">
      <div className="pol-name">
        <b>{t.title}</b>
        <span className="small">
          <span className="act">{t.action}</span> · {KIND_WORDS[t.kind] ?? t.kind}
        </span>
      </div>
      <div className="seg" role="group" aria-label={`Policy for ${t.title}`}>
        {POLICIES.map((p) => {
          const allowed = props.isOwner && canChoose(p, t.max, t.blocked);
          return (
            <button
              key={p}
              type="button"
              className={`btn quiet${t.current === p ? ' on' : ''}`}
              aria-pressed={t.current === p}
              // The current choice stays readable (not greyed out); pressing it again does nothing.
              disabled={!allowed && t.current !== p}
              title={
                !props.isOwner
                  ? 'Only the owner changes this'
                  : !canChoose(p, t.max, t.blocked)
                    ? `Above this action’s maximum (${POLICY_LABEL[t.max]})`
                    : undefined
              }
              onClick={() => {
                if (allowed && t.current !== p) onSet(t, p === t.default ? null : p);
              }}
            >
              {POLICY_LABEL[p]}
            </button>
          );
        })}
      </div>
      {/* Only what is worth reading: a plain "Default: X." on a row still at its default says nothing. */}
      {!(policyCaption(t) === `Default: ${POLICY_LABEL[t.default]}.` && t.current === t.default) && (
        <p className="cap small">
          {(t.blocked || t.max !== 'auto') && <Lock />}
          {policyCaption(t)}
        </p>
      )}
      {/* A count bites only on what runs on its own; a ceiling also labels a request that asks anyway. */}
      {t.limit && (t.current === 'auto' || (t.current === 'ask_first' && t.limit.hasAmount)) && (
        <LimitForm t={t} limit={t.limit} props={props} onSaved={onSaved} />
      )}
    </li>
  );
}

function BudgetForm({
  label,
  agentKey,
  budgetCents,
  spentCents,
  props,
  onSaved,
}: {
  label: string;
  agentKey: string;
  budgetCents: number | null;
  spentCents: number;
  props: AgentsProps;
  onSaved: () => Promise<void>;
}) {
  const [text, setText] = useState(budgetCents === null ? '' : String(budgetCents / 100));
  useEffect(() => setText(budgetCents === null ? '' : String(budgetCents / 100)), [agentKey, budgetCents]);
  const act = useAct(props.toast, onSaved);
  const saveClass = useDesign() === 'blueprint' ? 'btn soft' : 'btn primary';
  const parsed = parseBudget(text);
  const id = `budget-${agentKey === '*' ? 'company' : agentKey}`;
  return (
    <form
      className="fgrid inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (parsed === null) return;
        void act.run(
          'agents.setBudget',
          { agentKey, monthlyBudgetCents: parsed === 'none' ? null : parsed },
          parsed === 'none' ? `${label}: no cap.` : `${label}: ${money(parsed)} a month.`,
        );
      }}
    >
      <label htmlFor={id}>
        <span className="flabel">{label} ($ a month)</span>
        <input
          id={id}
          className="field"
          inputMode="decimal"
          value={text}
          disabled={!props.isOwner}
          placeholder="No cap"
          aria-invalid={parsed === null}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <p className="muted small">Spent this month: {money(spentCents)}</p>
      {props.isOwner && (
        <div className="row-actions">
          {/* Blueprint: one primary in a zone (DESIGN.md §3a), and Budget has three forms */}
          <button className={saveClass} type="submit" disabled={act.busy || parsed === null}>
            Save
          </button>
          {parsed === null && <span className="error small">Type an amount in dollars, like 120.</span>}
        </div>
      )}
    </form>
  );
}

/** The share of a budget at which the owner hears, once a month per agent (M10). */
function WarnForm({
  percent,
  props,
  onSaved,
}: {
  percent: number;
  props: AgentsProps;
  onSaved: () => Promise<void>;
}) {
  const [text, setText] = useState(String(percent));
  useEffect(() => setText(String(percent)), [percent]);
  const act = useAct(props.toast, onSaved);
  const saveClass = useDesign() === 'blueprint' ? 'btn soft' : 'btn primary';
  const parsed = parsePercent(text);
  return (
    <form
      className="fgrid inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (parsed !== null)
          void act.run(
            'settings.set',
            { key: WARN_KEY, value: parsed },
            `You’ll hear at ${parsed} % of a budget.`,
          );
      }}
    >
      <label htmlFor="budget-warn">
        <span className="flabel">Tell me at (% of a budget)</span>
        <input
          id="budget-warn"
          className="field"
          inputMode="numeric"
          value={text}
          disabled={!props.isOwner}
          aria-invalid={parsed === null}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <p className="muted small">Once a month for each agent, and for all of them against the cap.</p>
      {props.isOwner && (
        <div className="row-actions">
          {/* Blueprint: one primary in a zone (DESIGN.md §3a), and Budget has three forms */}
          <button className={saveClass} type="submit" disabled={act.busy || parsed === null}>
            Save
          </button>
          {parsed === null && <span className="error small">A whole number from 1 to 99.</span>}
        </div>
      )}
    </form>
  );
}

function HoursForm({ props }: { props: AgentsProps }) {
  const setting = useQuery<{ value: ReceptionistHours }>('settings.get', { key: HOURS_KEY });
  const [h, setH] = useState<ReceptionistHours | null>(null);
  useEffect(() => {
    if (setting.data) setH(setting.data.value);
  }, [setting.data]);
  const act = useAct(props.toast, () => setting.reload());
  if (!h)
    return <Loading error={setting.error} what="the answering hours" retry={() => void setting.reload()} />;
  const ro = !props.isOwner;
  return (
    <form
      className="fgrid hours-form"
      onSubmit={(e) => {
        e.preventDefault();
        void act.run('settings.set', { key: HOURS_KEY, value: h }, 'The receptionist’s hours are saved.');
      }}
    >
      <p className="wide small">{hoursText(h)}</p>
      <fieldset className="wide seg-field">
        <legend className="flabel">When it answers</legend>
        <div className="seg" role="group">
          {(
            [
              ['after_hours', 'After office hours'],
              ['always', 'Always'],
            ] as const
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              className={`btn quiet${h.mode === m ? ' on' : ''}`}
              aria-pressed={h.mode === m}
              disabled={ro && h.mode !== m}
              onClick={() => {
                if (!ro) setH({ ...h, mode: m });
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>
      <label>
        <span className="flabel">Office opens</span>
        <input
          className="field"
          type="time"
          value={h.open}
          disabled={ro || h.mode === 'always'}
          onChange={(e) => setH({ ...h, open: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Office closes</span>
        <input
          className="field"
          type="time"
          value={h.close}
          disabled={ro || h.mode === 'always'}
          onChange={(e) => setH({ ...h, close: e.target.value })}
        />
      </label>
      <fieldset className="wide days">
        <legend className="flabel">Office days</legend>
        {DAYS.map((d) => (
          <label key={d.n} className="tick">
            <input
              type="checkbox"
              checked={h.days.includes(d.n)}
              disabled={ro || h.mode === 'always'}
              onChange={(e) =>
                setH({
                  ...h,
                  days: e.target.checked ? [...h.days, d.n].sort() : h.days.filter((x) => x !== d.n),
                })
              }
            />
            {d.label}
          </label>
        ))}
      </fieldset>
      {props.isOwner && (
        <div className="row-actions wide">
          <button className="btn primary" type="submit" disabled={act.busy}>
            Save hours
          </button>
        </div>
      )}
    </form>
  );
}

function Memory({ agentKey, props }: { agentKey: string; props: AgentsProps }) {
  const notes = useQuery<{ notes: MemoryNote[] }>('memory.list', { agentKey });
  const act = useAct(props.toast, () => notes.reload());
  return (
    <Panel title="Its notes" aside={<span className="chip">{notes.data?.notes.length ?? 0}</span>}>
      <p className="muted small">
        What the agent wrote down for itself. Only agents write notes; you can take one out (it stays in the
        history).
      </p>
      {!notes.data ? (
        <Loading error={notes.error} what="its notes" retry={() => void notes.reload()} />
      ) : notes.data.notes.length === 0 ? (
        <Empty>No notes.</Empty>
      ) : (
        <ul className="rows">
          {notes.data.notes.map((n) => (
            <li key={n.id} className="note-row">
              <span className="small">{n.body}</span>
              <span className="muted small">
                {new Date(n.writtenAt).toLocaleDateString('en-CA', { dateStyle: 'medium' })}
              </span>
              <button
                type="button"
                className="btn quiet"
                disabled={act.busy}
                onClick={() => void act.run('memory.remove', { id: n.id }, 'The note is out of its memory.')}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function Settings({
  hoursElsewhere = false,
  ...props
}: AgentsProps & {
  /** Blueprint's Setup: the receptionist's hours are under Calls, beside the calls it answers. */
  hoursElsewhere?: boolean;
}) {
  const pol = useQuery<AgentPolicies>('agents.policies', {});
  const list = useQuery<AgentsList>('agents.list', {});
  const [key, setKey] = useState(props.agentKey);
  const act = useAct(props.toast, () => pol.reload());
  if (!pol.data || !list.data)
    return (
      <Loading
        error={pol.error ?? list.error}
        what="the agent settings"
        retry={() => void Promise.all([pol.reload(), list.reload()])}
      />
    );
  const agent = pol.data.agents.find((a) => a.key === key) ?? pol.data.agents[0]!;
  const summary = list.data.agents.find((a) => a.key === agent.key)!;
  const setPolicy = (t: ToolPolicy, p: Policy | null) =>
    void act.run(
      p === null ? 'settings.clear' : 'settings.set',
      p === null ? { key: `agents.policy.${t.action}` } : { key: `agents.policy.${t.action}`, value: p },
      `${t.title}: ${POLICY_LABEL[p ?? t.default]} for every agent.`,
    );
  return (
    <div className="books-page">
      <div className="page-head">
        <h2 className="page-title">Agent settings</h2>
        <p className="muted">
          For each action an agent can take: <b>automatic</b>, <b>ask first</b> or <b>never</b>. You can
          tighten anything, and loosen only up to the action’s maximum. A setting is per action, so it applies
          to every agent that has that action. Every change is logged with who made it.
        </p>
        {!props.isOwner && (
          <span className="chip">
            <Lock /> Read-only: the owner changes these
          </span>
        )}
      </div>
      <div className="seg wrap" role="group" aria-label="Agent">
        {pol.data.agents.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`btn quiet${a.key === agent.key ? ' on' : ''}`}
            aria-pressed={a.key === agent.key}
            onClick={() => setKey(a.key)}
          >
            <span className="agav sm" aria-hidden="true">
              {initials(a.name)}
            </span>
            {a.name}
          </button>
        ))}
      </div>
      <Panel title={agent.name} aside={<span className="chip say">Acts as {roleLabel(agent.role)}</span>}>
        <p className="muted small">It sees only the actions below: its role’s, narrowed to its own list.</p>
        <ul className="rows pols">
          {agent.tools.map((t) => (
            <PolicyRow key={t.action} t={t} props={props} onSet={setPolicy} onSaved={() => pol.reload()} />
          ))}
        </ul>
      </Panel>
      <div className="split even">
        <Panel title="Budget">
          <BudgetForm
            label={agent.name}
            agentKey={agent.key}
            budgetCents={summary.budgetCents}
            spentCents={summary.spentCents}
            props={props}
            onSaved={() => list.reload()}
          />
          <BudgetForm
            label="All agents together"
            agentKey="*"
            budgetCents={list.data.company.budgetCents}
            spentCents={list.data.company.spentCents}
            props={props}
            onSaved={() => list.reload()}
          />
          <WarnForm percent={pol.data.warnPercent} props={props} onSaved={() => pol.reload()} />
          <p className="muted small">At the cap the agent stops until next month.</p>
        </Panel>
        {agent.key === 'receptionist' && !hoursElsewhere ? (
          <Panel title="When the receptionist answers">
            <HoursForm props={props} />
          </Panel>
        ) : (
          <Memory agentKey={agent.key} props={props} />
        )}
      </div>
      {agent.key === 'receptionist' && !hoursElsewhere && <Memory agentKey={agent.key} props={props} />}
      {agent.key === 'receptionist' && hoursElsewhere && (
        <Moved what="When the receptionist answers" to="Setup › Calls" href="#/settings/calls" />
      )}
    </div>
  );
}

/** The agents' props for a page outside the Agents screen (Setup's AI agents, Calls): its own toast. */
function useOwnProps(held: readonly string[]): [AgentsProps, string | null] {
  const [toast, setToast] = useToast();
  return [
    {
      // agents.manage's, which stays with the Owner role
      isOwner: held.includes('agents.manage'),
      seesAgents: held.includes('agents.view'),
      toast: setToast,
      // the first agent, as the Agents screen opens on
      agentKey: '',
      go: () => undefined,
    },
    toast,
  ];
}

const Toast = ({ toast }: { toast: string | null }) =>
  toast ? (
    <div className="toast" role="status">
      {toast}
    </div>
  ) : null;

/** Setup › AI agents (Blueprint, the setup audit): what each agent may do, its budget and its notes. */
export function AgentSettingsPage({ held }: { held: readonly string[] }) {
  const [props, toast] = useOwnProps(held);
  // the Agents screen's own wrappers, so its Blueprint styles hold here too (blueprint/inbox.css); Setup's
  // page is already the <main>, so its body is a plain block, never a second main (QA-879)
  return (
    <div className="books agents">
      <div className="books-body bp-ib">
        <div className="books-main">
          <Settings {...props} hoursElsewhere />
        </div>
      </div>
      <Toast toast={toast} />
    </div>
  );
}
